import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bot, MessageSquare, Phone, Database, FileText, TrendingUp, Users, Building2, Brain, DollarSign, Workflow, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const AIProducts = () => {
  const aiProducts = [
    {
      id: 1,
      icon: 'Bot',
      title: 'AI Business Agents',
      subtitle: 'Intelligent Digital Workers for Your Business',
      description: 'AI Agents are the next evolution beyond traditional chatbots. Instead of simply answering questions, an AI agent can understand a request, access information, make decisions and perform actions.',
      useCases: ['Sales Agents', 'Customer Service Agents', 'Finance Agents', 'HR Agents', 'Procurement Agents', 'Operations Agents', 'Knowledge Agents', 'Research Agents', 'Reporting Agents', 'Executive Assistants'],
      example: 'An AI Sales Agent can receive a customer enquiry, understand the requirement, check product information, qualify the lead, check CRM information, prepare a quotation, schedule a meeting, update the CRM, and follow up automatically.',
      gradient: 'from-purple-500 to-blue-600'
    },
    {
      id: 2,
      icon: 'MessageSquare',
      title: 'AI-Powered WhatsApp Automation',
      subtitle: 'Turn WhatsApp into an Intelligent Business Channel',
      description: 'WhatsApp has become one of the most important customer communication channels for businesses. We build AI-powered WhatsApp solutions that can understand customer questions, retrieve information and provide intelligent responses.',
      useCases: ['Order Status Queries', 'Product Catalogue Sharing', 'Customer Support', 'Lead Qualification', 'Appointment Scheduling', 'Payment Reminders'],
      example: 'Customer: "Where is my order?" → AI Agent: "Your order #4587 has been dispatched and is expected to arrive tomorrow."',
      integrations: ['CRM', 'ERP', 'Inventory', 'Order Management', 'Customer Database', 'Knowledge Base', 'Google Sheets', 'Excel', 'SQL Databases', 'Websites'],
      gradient: 'from-green-500 to-emerald-600'
    },
    {
      id: 3,
      icon: 'Phone',
      title: 'AI Voice Agents',
      subtitle: 'Intelligent Voice Conversations — 24×7',
      description: 'Not every customer wants to type. Our AI Voice Agents allow businesses to automate conversations over the phone using Speech Recognition + AI + Voice Synthesis + Business Systems.',
      useCases: {
        inbound: ['Answer calls', 'Understand customer questions', 'Retrieve information', 'Create support tickets', 'Check order status', 'Schedule appointments', 'Transfer calls to employees', 'Capture customer information'],
        outbound: ['Lead follow-ups', 'Payment reminders', 'Appointment reminders', 'Customer surveys', 'Sales campaigns', 'Renewal reminders', 'Service notifications', 'Customer verification']
      },
      result: 'Your business can have conversations with hundreds or thousands of customers without increasing your call-centre headcount proportionally.',
      gradient: 'from-blue-500 to-indigo-600'
    },
    {
      id: 4,
      icon: 'Database',
      title: 'Retrieval-Augmented Generation (RAG)',
      subtitle: 'Give AI Access to Your Company\'s Knowledge',
      description: 'Generic AI knows a lot. But it doesn\'t automatically know your company\'s policies, products, contracts, procedures, manuals and internal knowledge. RAG allows us to connect AI with your proprietary knowledge.',
      sources: ['PDF documents', 'Word documents', 'Excel files', 'Websites', 'Product catalogues', 'SOPs', 'Policy documents', 'Training manuals', 'Contracts', 'Accounting documents', 'Technical documentation', 'Industry research', 'Internal databases'],
      example: 'Instead of searching through 200 PDF files, employees can ask: "What is our approval process for purchases above ₹10 lakh?" The AI searches the relevant company documents and provides the answer with appropriate context.',
      gradient: 'from-violet-500 to-purple-600'
    },
    {
      id: 5,
      icon: 'FileText',
      title: 'AI Document Intelligence',
      subtitle: 'Convert Documents into Structured Business Data',
      description: 'Businesses process thousands of documents every month. Invoices, Purchase orders, Contracts, Bank statements, Expense claims, Tax documents, Application forms. Traditional data entry is slow and expensive. AI can understand documents and automatically extract the information you need.',
      capabilities: ['Read PDFs', 'Extract structured information', 'Understand tables', 'Extract invoice information', 'Classify documents', 'Validate information', 'Identify missing fields', 'Compare documents', 'Summarise contracts', 'Send extracted information into ERP/CRM systems'],
      example: 'Invoice PDF → AI → Validation → ERP → Approval → Accounting',
      objective: 'Less data entry. Fewer errors. Faster processing.',
      gradient: 'from-orange-500 to-red-600'
    },
    {
      id: 6,
      icon: 'DollarSign',
      title: 'AI for Finance & Accounting',
      subtitle: 'Make Finance More Intelligent',
      description: 'Finance teams spend significant time collecting data, reconciling information, preparing reports and responding to repetitive queries. AI can transform Finance from a transaction-processing function into a more intelligent decision-support function.',
      useCases: ['Invoice processing', 'Expense classification', 'Payment follow-ups', 'Receivables analysis', 'Payables analysis', 'Cash-flow analysis', 'Financial reporting', 'Variance analysis', 'Management reporting', 'Financial document analysis', 'Accounting knowledge assistant', 'Month-end close assistance', 'CFO dashboards', 'Finance Q&A'],
      examples: ['Which customers have overdue balances above ₹10 lakh?', 'Why has gross margin declined this month?', 'Which cost centres exceeded budget?', 'What are our top five cash-flow risks?'],
      gradient: 'from-emerald-500 to-green-600'
    },
    {
      id: 7,
      icon: 'TrendingUp',
      title: 'AI for Sales & CRM',
      subtitle: 'Turn Your CRM into an Intelligent Sales Assistant',
      description: 'Your CRM contains valuable information about leads, customers, opportunities, activities and sales history. AI can make that information actionable.',
      capabilities: ['Lead qualification', 'Lead scoring', 'Customer segmentation', 'Email generation', 'Follow-up automation', 'Meeting preparation', 'Sales opportunity analysis', 'Proposal generation', 'Customer research', 'CRM data enrichment', 'Sales forecasting', 'Next-best-action recommendations'],
      aiAgent: 'An AI sales agent can work alongside your sales team — researching prospects, answering questions, preparing information and ensuring that leads don\'t fall through the cracks.',
      gradient: 'from-blue-500 to-cyan-600'
    },
    {
      id: 8,
      icon: 'Users',
      title: 'AI for Customer Service',
      subtitle: 'From Ticket Handling to Intelligent Customer Resolution',
      description: 'Customers expect fast answers. But traditional support models depend heavily on human availability. Our AI customer-service solutions can provide 24×7 first-level support while escalating complex issues to human employees.',
      capabilities: ['Answer FAQs', 'Search knowledge bases', 'Understand customer history', 'Check order status', 'Create support tickets', 'Classify complaints', 'Route tickets', 'Suggest solutions', 'Escalate critical issues', 'Analyse customer conversations'],
      model: 'AI handles repetitive interactions. Humans handle judgement, relationships and exceptions.',
      gradient: 'from-pink-500 to-rose-600'
    },
    {
      id: 9,
      icon: 'Users',
      title: 'AI for Human Resources',
      subtitle: 'Build an Intelligent HR Function',
      description: 'HR teams manage large amounts of employee information and repetitive processes. AI can help HR teams automate employee interactions while keeping sensitive information controlled.',
      useCases: ['Employee HR assistant', 'Policy Q&A', 'Recruitment screening', 'Job description generation', 'Candidate communication', 'Interview scheduling', 'Employee onboarding', 'Training assistants', 'Leave and attendance queries', 'HR document search', 'Employee FAQ automation'],
      examples: ['How many days of leave can I carry forward?', 'What is our work-from-home policy?', 'What documents do I need for reimbursement?'],
      gradient: 'from-indigo-500 to-purple-600'
    },
    {
      id: 10,
      icon: 'Building2',
      title: 'AI + ERP',
      subtitle: 'Make Your ERP Intelligent',
      description: 'ERP systems contain some of the most valuable information in an organisation. We help businesses bring AI capabilities into their existing ERP environment.',
      platforms: ['Odoo', 'Zoho', 'Microsoft Dynamics', 'QuickBooks', 'Xero', 'SAP', 'Custom ERP systems'],
      modules: ['Finance', 'Sales', 'Purchasing', 'Inventory', 'Manufacturing', 'Projects', 'CRM', 'HR', 'Reporting'],
      examples: ['Which suppliers have increased prices by more than 10% this quarter?', 'Show me customers whose payments are consistently delayed.', 'Which products are approaching stock-out levels?', 'Which projects are exceeding budget?'],
      benefit: 'Instead of navigating multiple screens and reports, management can interact with business data using natural language.',
      gradient: 'from-cyan-500 to-blue-600'
    },
    {
      id: 11,
      icon: 'BarChart3',
      title: 'AI Business Intelligence',
      subtitle: 'From Dashboards to Decision Intelligence',
      description: 'Traditional dashboards tell you what happened. AI can help explain: What happened? Why did it happen? What is likely to happen next? What should we do?',
      insights: ['Revenue trends', 'Margin deterioration', 'Customer churn', 'Sales pipeline risks', 'Working-capital issues', 'Cost anomalies', 'Procurement opportunities', 'Inventory problems', 'Operational inefficiencies'],
      goal: 'Move from Data → Reports to Data → Insight → Recommendation → Action',
      gradient: 'from-yellow-500 to-orange-600'
    },
    {
      id: 12,
      icon: 'Workflow',
      title: 'AI Automation for Business Processes',
      subtitle: 'Don\'t Just Automate Tasks. Automate the Process.',
      description: 'The biggest opportunity often isn\'t a single task. It is the complete business workflow. AI can assist at multiple points in the process — reading documents, checking information, identifying exceptions and triggering actions.',
      workflows: {
        procureToPay: ['Purchase Request', 'Approval', 'Purchase Order', 'Goods Receipt', 'Invoice', 'Three-Way Match', 'Payment'],
        orderToCash: ['Lead', 'Quotation', 'Sales Order', 'Delivery', 'Invoice', 'Collection']
      },
      benefit: 'AI can support the complete customer lifecycle.',
      gradient: 'from-red-500 to-pink-600'
    }
  ];

  const getIcon = (iconName) => {
    const icons = {
      Bot, MessageSquare, Phone, Database, FileText, DollarSign, TrendingUp, Users, Building2, BarChart3, Workflow
    };
    return icons[iconName] || Brain;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-purple-400 rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">Our Artificial Intelligence Solutions</h1>
            <p className="text-xl md:text-2xl text-purple-200 mb-8">
              Comprehensive AI products and services designed to transform every aspect of your business operations
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-purple-900 hover:bg-gray-100 text-lg px-8 py-6">
                Build Your First AI Agent <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* AI Products Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto space-y-20">
            {aiProducts.map((product, index) => {
              const IconComponent = getIcon(product.icon);
              const isEven = index % 2 === 0;
              
              return (
                <Card key={product.id} className={`border-none shadow-2xl overflow-hidden ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                  <div className="lg:flex">
                    {/* Icon/Number Section */}
                    <div className={`lg:w-1/3 bg-gradient-to-br ${product.gradient} p-12 flex flex-col items-center justify-center text-white`}>
                      <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
                        <IconComponent className="h-12 w-12" />
                      </div>
                      <div className="text-6xl font-bold opacity-50 mb-4">{String(product.id).padStart(2, '0')}</div>
                      <h3 className="text-2xl font-bold text-center">{product.title}</h3>
                    </div>
                    
                    {/* Content Section */}
                    <CardContent className="lg:w-2/3 p-8 md:p-12">
                      <p className="text-sm font-semibold text-purple-600 mb-2">{product.subtitle}</p>
                      <p className="text-gray-700 mb-6 text-lg">{product.description}</p>
                      
                      {/* Use Cases */}
                      {product.useCases && Array.isArray(product.useCases) && (
                        <div className="mb-6">
                          <h4 className="font-bold text-gray-900 mb-3">Use Cases:</h4>
                          <div className="grid grid-cols-2 gap-2">
                            {product.useCases.map((useCase, idx) => (
                              <div key={idx} className="flex items-center space-x-2">
                                <div className="w-1.5 h-1.5 bg-purple-600 rounded-full"></div>
                                <span className="text-sm text-gray-600">{useCase}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Inbound/Outbound for Voice */}
                      {product.useCases && typeof product.useCases === 'object' && !Array.isArray(product.useCases) && (
                        <div className="mb-6 grid md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="font-bold text-gray-900 mb-3">Inbound Automation:</h4>
                            <ul className="space-y-1">
                              {product.useCases.inbound.map((item, idx) => (
                                <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                                  <span className="text-purple-600 mt-1">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-3">Outbound Automation:</h4>
                            <ul className="space-y-1">
                              {product.useCases.outbound.map((item, idx) => (
                                <li key={idx} className="flex items-start space-x-2 text-sm text-gray-600">
                                  <span className="text-purple-600 mt-1">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}

                      {/* Sources/Capabilities/Platforms */}
                      {(product.sources || product.capabilities || product.platforms || product.modules) && (
                        <div className="mb-6">
                          <h4 className="font-bold text-gray-900 mb-3">
                            {product.sources ? 'Knowledge Sources:' : product.capabilities ? 'Capabilities:' : product.platforms ? 'Supported Platforms:' : 'ERP Modules:'}
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {(product.sources || product.capabilities || product.platforms || product.modules)?.map((item, idx) => (
                              <span key={idx} className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Integrations */}
                      {product.integrations && (
                        <div className="mb-6">
                          <h4 className="font-bold text-gray-900 mb-3">Can be connected to:</h4>
                          <div className="flex flex-wrap gap-2">
                            {product.integrations.map((integration, idx) => (
                              <span key={idx} className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                                {integration}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Workflows */}
                      {product.workflows && (
                        <div className="mb-6 space-y-4">
                          <div>
                            <h4 className="font-bold text-gray-900 mb-2">Procure-to-Pay:</h4>
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                              {product.workflows.procureToPay.map((step, idx) => (
                                <React.Fragment key={idx}>
                                  <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded">{step}</span>
                                  {idx < product.workflows.procureToPay.length - 1 && <span className="text-gray-400">→</span>}
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-2">Order-to-Cash:</h4>
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                              {product.workflows.orderToCash.map((step, idx) => (
                                <React.Fragment key={idx}>
                                  <span className="px-3 py-1 bg-green-50 text-green-700 rounded">{step}</span>
                                  {idx < product.workflows.orderToCash.length - 1 && <span className="text-gray-400">→</span>}
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Example */}
                      {product.example && (
                        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 mb-4">
                          <p className="text-sm font-semibold text-gray-700 mb-1">Example:</p>
                          <p className="text-gray-600 italic">{product.example}</p>
                        </div>
                      )}

                      {/* Examples (multiple) */}
                      {product.examples && (
                        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 mb-4">
                          <p className="text-sm font-semibold text-gray-700 mb-2">Ask Your Data:</p>
                          <ul className="space-y-1">
                            {product.examples.map((ex, idx) => (
                              <li key={idx} className="text-sm text-gray-600 italic">• "{ex}"</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Result/Objective/Benefit/Goal/Model/AI Agent */}
                      {(product.result || product.objective || product.benefit || product.goal || product.model || product.aiAgent) && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg border-l-4 border-purple-600">
                          <p className="text-gray-700 font-medium">
                            {product.result || product.objective || product.benefit || product.goal || product.model || product.aiAgent}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our AI Technology Stack</h2>
              <p className="text-lg text-gray-600">
                We work with modern AI technologies and integrate them with the systems businesses already use.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="border-none shadow-lg bg-white">
                <CardContent className="p-6">
                  <Brain className="h-10 w-10 text-purple-600 mb-4" />
                  <h3 className="font-bold text-gray-900 mb-3">AI & LLMs</h3>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Large Language Models</li>
                    <li>• Generative AI</li>
                    <li>• AI Agents</li>
                    <li>• Natural Language Processing</li>
                    <li>• Conversational AI</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-white">
                <CardContent className="p-6">
                  <Database className="h-10 w-10 text-blue-600 mb-4" />
                  <h3 className="font-bold text-gray-900 mb-3">Knowledge & RAG</h3>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Retrieval-Augmented Generation</li>
                    <li>• Vector Databases</li>
                    <li>• Semantic Search</li>
                    <li>• Document Intelligence</li>
                    <li>• Knowledge Bases</li>
                    <li>• Embeddings</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-white">
                <CardContent className="p-6">
                  <Workflow className="h-10 w-10 text-green-600 mb-4" />
                  <h3 className="font-bold text-gray-900 mb-3">Automation</h3>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Workflow Automation</li>
                    <li>• API Integrations</li>
                    <li>• ERP Integration</li>
                    <li>• CRM Integration</li>
                    <li>• WhatsApp Integration</li>
                    <li>• Voice Automation</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-white">
                <CardContent className="p-6">
                  <Building2 className="h-10 w-10 text-orange-600 mb-4" />
                  <h3 className="font-bold text-gray-900 mb-3">Enterprise Platforms</h3>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Zoho</li>
                    <li>• Odoo</li>
                    <li>• Microsoft Dynamics</li>
                    <li>• QuickBooks</li>
                    <li>• Xero</li>
                    <li>• SAP</li>
                    <li>• Custom Applications</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Build Your First AI Business Agent</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto text-purple-100">
            Let's identify where AI can create the highest ROI in your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100 text-lg px-8 py-6">
                Talk to Our AI Team <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/ai-solutions">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-purple-600 text-lg px-8 py-6">
                Learn About Our Approach
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIProducts;